import { Badge } from "@components/ui/Badge"
import { Button } from "@components/ui/Button"
import { Inset } from "@components/ui/Card"
import { AdaptiveLevelBadge } from "@features/adaptive-levels"
import { useCutFormat } from "@hooks/useCutFormat"
import { attentionCount } from "@lib/attention"
import { cn } from "@lib/cn"
import { formatDate, formatGrade, formatPercent, getMexicoCityToday } from "@lib/intl"
import { isPhotoGradeableMaterialType } from "@lib/materials"
import { AlertTriangle, ArrowRight, Camera, Check, ChevronDown } from "lucide-react"
import { memo, useId, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import type { GradeScale, ProfileEnrollment, StudentCutAverage, StudentTaskHistory } from "@/types"

/**
 * What a row still owes, and to whom.
 *
 * Derived from the three fields the PROFILE payload actually carries
 * (`status`, `delivered`, `grade`) — `api/student_profiles.py` does not
 * populate `pending_upload`, so this card must never claim an "en revisión"
 * state it cannot see. The derivation order mirrors
 * `features/students/components/StudentTasksList.tsx::deriveStatus` on
 * purpose: an `awaiting` record carries `delivered=False` by model default,
 * so testing `delivered === false` before `status === "awaiting"` would
 * report every open task as missing. Duplicated rather than imported
 * because a cross-feature import is banned (architecture.md).
 */
const UNGRADED_BADGE = {
  // The student owes the work.
  not_delivered: "danger",
  // The professor owes the grade.
  pending_grading: "attention",
  // Sent, still open: nobody is late yet.
  awaiting: "outline",
  // The task exists but no record was ever created and it is not yet due.
  // Nothing to act on from here, so it reads as settled.
  no_record: "neutral",
} as const

type UngradedState = keyof typeof UNGRADED_BADGE
type RowState = UngradedState | "graded"

/** Sort order inside the card: whoever is blocked longest-standing first. */
const ROW_RANK: Record<RowState, number> = {
  not_delivered: 0,
  pending_grading: 1,
  awaiting: 2,
  graded: 3,
  no_record: 4,
}

/** Rank at which a row stops being pending and becomes history. */
const SETTLED_RANK = ROW_RANK.graded

/** Pending rows shown before the "ver N más" toggle lifts the cap. */
const PENDING_PREVIEW = 3

const isUngraded = (state: RowState): state is UngradedState => state !== "graded"

interface HistoryRow {
  task: StudentTaskHistory
  state: RowState
}

/** One heading of the history: a cut with this student's average in it, or
 *  (cut null) the rows that fall outside every cut. */
interface HistoryGroup {
  key: string
  cut: StudentCutAverage | null
  rows: HistoryRow[]
}

/**
 * PRD-61 §FR-5.2 — the settled history under one heading per evaluation cut,
 * newest cut first; rows outside every cut (or in a cut the payload does not
 * list) go last under "Resto del curso". Null when the school has no
 * calendar, and the history stays the flat list it always was.
 */
const groupByCut = (rows: HistoryRow[], cuts: StudentCutAverage[] | undefined) => {
  if (!cuts || cuts.length === 0) return null
  const known = new Set(cuts.map((c) => c.number))
  const byCut = new Map<number, HistoryRow[]>()
  const outside: HistoryRow[] = []
  for (const row of rows) {
    const cut = row.task.cut ?? null
    if (cut === null || !known.has(cut)) outside.push(row)
    else byCut.set(cut, [...(byCut.get(cut) ?? []), row])
  }
  const groups: HistoryGroup[] = [...cuts]
    .sort((a, b) => b.number - a.number)
    .filter((cut) => byCut.has(cut.number))
    .map((cut) => ({ key: `cut-${cut.number}`, cut, rows: byCut.get(cut.number) ?? [] }))
  if (outside.length > 0) groups.push({ key: "outside", cut: null, rows: outside })
  return groups
}

const rowState = (task: StudentTaskHistory, today: string): RowState => {
  if (task.grade !== null) return "graded"
  if (task.status === "awaiting") return "awaiting"
  if (task.status === "pending_grading" || task.delivered === true) return "pending_grading"
  if (task.status === "not_delivered" || task.delivered === false) return "not_delivered"
  // No record and past due: nothing came in, so the student owes it. Filed as
  // history it read "Sin pendientes" beside "Entregas 0/1" (PRD-59 Etapa B).
  // `due_date` is `YYYY-MM-DD`, so a string compare is a date compare.
  if (task.due_date && task.due_date < today) return "not_delivered"
  return "no_record"
}

const TaskRow = memo(function TaskRow({
  task,
  state,
  scale,
  classroomId,
  studentId,
}: {
  task: StudentTaskHistory
  state: RowState
  scale: GradeScale
  classroomId: string
  studentId: string
}) {
  const { t } = useTranslation("student-profile")

  // "Calificar con foto" for a row nobody has graded yet, on a material whose
  // sheet can actually be photographed (`lib/materials` owns that set).
  //
  // The affordance existed on the legacy per-subject page and vanished with
  // it: that page opens `PhotoGradingModal`, and it has been unreachable
  // since `Student.profile` became non-nullable — `StudentDetailRedirect`
  // always finds a profile, so its fallback never renders. Which is just as
  // well, because that modal never sent `force`, so a cell that gained a
  // grade after the page loaded answered 409 and asked her to confirm with a
  // control that did not exist.
  //
  // So the row hands off to `/herramientas/escanear` — the surface that owns
  // multi-page capture, real progress, the overwrite confirmation and the
  // review queue — with the target pre-picked (PRD-59).
  const canPhotoGrade =
    state === "not_delivered" || state === "awaiting" || state === "pending_grading"
  const scanHref = `/herramientas/escanear?salon=${classroomId}&tarea=${task.task_id}&alumno=${studentId}`
  // `material_title` is omitted on purpose: `task_name` is derived from it,
  // so printing both reads as a stutter on a 320px-wide card.
  const body = (
    <>
      <span className="truncate text-sm font-medium">{task.task_name}</span>
      <span className="text-meta truncate">
        {t("card.tasks.due", { date: formatDate(task.due_date) })}
      </span>
    </>
  )

  return (
    <li className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        {/* Hand-typed tasks have no material behind them, so nothing to open. */}
        {task.material_id ? (
          <Link
            to={`/materiales/${task.material_id}`}
            className="rounded-control text-ink hover:text-accent-ink flex min-h-11 flex-col justify-center transition-colors duration-150 md:min-h-9"
          >
            {body}
          </Link>
        ) : (
          <div className="text-ink flex min-h-11 flex-col justify-center md:min-h-9">{body}</div>
        )}
      </div>
      {task.grade !== null ? (
        <span className="text-ink shrink-0 text-sm font-semibold" data-numeric>
          {formatGrade(task.grade, scale)}
        </span>
      ) : (
        // Never colour alone (accessibility.md §5): the badge always says the
        // state in words, and the tone only reinforces it.
        isUngraded(state) && (
          <div className="flex shrink-0 items-center gap-1">
            {canPhotoGrade && isPhotoGradeableMaterialType(task.material_type) ? (
              <Link
                to={scanHref}
                aria-label={t("card.tasks.photo_grade_aria", { task: task.task_name })}
                title={t("card.tasks.photo_grade")}
                className="rounded-control text-ink-muted hover:bg-inset hover:text-ink flex h-11 w-11 items-center justify-center transition-colors duration-150 md:h-8 md:w-8"
              >
                <Camera className="h-4 w-4" aria-hidden="true" />
              </Link>
            ) : null}
            <Badge variant={UNGRADED_BADGE[state]}>{t(`card.tasks.state.${state}`)}</Badge>
          </div>
        )
      )}
    </li>
  )
})

interface Props {
  enrollment: ProfileEnrollment
  scale: GradeScale
  /** Highlighted because the professor arrived from this subject. */
  highlighted?: boolean
  onReviewAdaptive?: (studentId: string, classroomId: string) => void
}

export const SubjectCard = memo(function SubjectCard({
  enrollment,
  scale,
  highlighted,
  onReviewAdaptive,
}: Props) {
  const { t } = useTranslation(["student-profile", "adaptive-levels"])
  const cutFormat = useCutFormat()
  const navigate = useNavigate()
  const [showAllPending, setShowAllPending] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const historyId = useId()

  /**
   * AFFORDANCE — two disclosures, no navigation, no modal.
   *
   * This card used to answer "what does this kid owe me in this subject?"
   * with a number that named nothing ("4 tareas sin entregar"), so the
   * professor had to leave the page to find out which four — even though the
   * payload already carried every row.
   *
   * A card in a two-up grid cannot be a table: twelve materials would make
   * one card three times the height of its neighbour. So the rows split by
   * who is blocked, and each half gets the affordance it earns:
   *
   *   · Pending rows (missing → to grade → still open) are ALWAYS visible,
   *     capped at PENDING_PREVIEW with an in-place toggle that lifts the cap.
   *     Three rows is about the height of the figures above them, so two
   *     side-by-side cards stay comparable at a glance.
   *   · Settled rows sit behind ONE collapsed disclosure, because they answer
   *     a different question — "how has he been doing" — which the grades
   *     chart under this grid already answers.
   *
   * Not a modal: nothing here needs protected focus, and a modal would hide
   * the sibling subjects the professor is comparing against.
   */
  const { pending, settled } = useMemo(() => {
    const today = formatDate(getMexicoCityToday(), "yyyy-MM-dd")
    const rows = enrollment.tasks.map((task) => ({ task, state: rowState(task, today) }))
    return {
      pending: rows
        .filter((row) => ROW_RANK[row.state] < SETTLED_RANK)
        // Oldest debt first inside each severity band: a three-week-old
        // missing exam has gone unaddressed longer than yesterday's.
        // `due_date` is `YYYY-MM-DD`, so a string compare is a date compare.
        .sort(
          (a, b) =>
            ROW_RANK[a.state] - ROW_RANK[b.state] || a.task.due_date.localeCompare(b.task.due_date),
        ),
      settled: rows
        .filter((row) => ROW_RANK[row.state] >= SETTLED_RANK)
        .sort((a, b) => b.task.due_date.localeCompare(a.task.due_date)),
    }
  }, [enrollment.tasks])

  const historyGroups = useMemo(
    () => groupByCut(settled, enrollment.cuts),
    [settled, enrollment.cuts],
  )

  const { visiblePending, hiddenPending } = useMemo(() => {
    const cap = showAllPending ? pending.length : PENDING_PREVIEW
    return {
      visiblePending: pending.slice(0, cap),
      hiddenPending: Math.max(0, pending.length - cap),
    }
  }, [pending, showAllPending])

  return (
    <article
      className={cn(
        "rounded-card border-border bg-surface shadow-raised flex min-w-0 flex-col border p-4",
        enrollment.is_at_risk && "border-danger/40",
        highlighted && "ring-accent ring-offset-canvas ring-2 ring-offset-2",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-ink truncate text-sm font-semibold">{enrollment.classroom_name}</h3>
          <p className="text-meta truncate">{enrollment.subject_name}</p>
        </div>
        {/* PRD-58 §FR-5.3 — the reason is spelled out in the line below, not
            hidden in a `title` only a mouse can reach (accessibility.md §5). */}
        {enrollment.is_at_risk && (
          <Badge variant="danger" className="shrink-0">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {t("card.at_risk")}
          </Badge>
        )}
        {enrollment.adaptive_level && onReviewAdaptive ? (
          // PRD-48 — alone on this card, so the badge says what a level
          // DOES. A title only reaches a mouse; on a phone the badge is the
          // button that opens her detail, which leads with the same sentence.
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => onReviewAdaptive(enrollment.student_id, enrollment.classroom_id)}
            className="focus-visible:ring-accent -m-2 inline-flex min-h-11 shrink-0 items-center rounded-full p-2 focus:outline-none focus-visible:ring-2 md:m-0 md:min-h-0 md:p-0"
          >
            <AdaptiveLevelBadge
              level={enrollment.adaptive_level.current_level}
              pinned={enrollment.adaptive_level.is_pinned}
              explain
            />
          </button>
        ) : enrollment.adaptive_level ? (
          <AdaptiveLevelBadge
            level={enrollment.adaptive_level.current_level}
            pinned={enrollment.adaptive_level.is_pinned}
            explain
          />
        ) : null}
      </div>

      {enrollment.is_at_risk && enrollment.attention_reason ? (
        <p className="text-danger-ink mt-1 text-xs">
          {t(`card.attention.reason.${enrollment.attention_reason}`, {
            count: attentionCount(enrollment.attention_reason, enrollment.attention_n),
          })}
        </p>
      ) : null}

      <p className="text-figure text-ink mt-3" data-numeric>
        {enrollment.avg_grade > 0 ? formatGrade(enrollment.avg_grade, scale) : "—"}
      </p>
      <p className="text-meta">
        {t("card.vs_group", {
          // No grades in the group yet: an average of 0.0 would be a claim.
          value:
            enrollment.classroom_avg_grade > 0
              ? formatGrade(enrollment.classroom_avg_grade, scale)
              : "—",
        })}
      </p>
      <p className="text-meta mt-0.5">
        {t("card.deliveries")}{" "}
        <span className="text-ink font-medium" data-numeric>
          {enrollment.delivered_count}/{enrollment.total_tasks}
        </span>
        {/* PRD-58 §FR-4.4 — no percentage over an empty denominator.
            `delivery_rate` is already 0-100; formatPercent divides. */}
        {enrollment.total_tasks > 0 && <> ({formatPercent(enrollment.delivery_rate)})</>}
      </p>

      {enrollment.total_tasks === 0 ? (
        <p className="text-meta mt-3">{t("card.tasks.none")}</p>
      ) : (
        <>
          <Inset className="mt-3 px-3 py-2">
            {pending.length === 0 ? (
              <p className="text-success-ink flex min-h-9 items-center gap-1.5 text-xs font-medium">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                {t("card.tasks.all_clear")}
              </p>
            ) : (
              <>
                <h4 className="text-label">
                  {t("card.tasks.pending_title", { count: pending.length })}
                </h4>
                <ul className="divide-hairline divide-y">
                  {visiblePending.map(({ task, state }) => (
                    <TaskRow
                      key={task.task_id}
                      task={task}
                      state={state}
                      scale={scale}
                      classroomId={enrollment.classroom_id}
                      studentId={enrollment.student_id}
                    />
                  ))}
                </ul>
                {hiddenPending > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-mx-2.5 mt-1"
                    onClick={() => setShowAllPending(true)}
                  >
                    {t("card.tasks.show_more_pending", { count: hiddenPending })}
                  </Button>
                )}
              </>
            )}
          </Inset>

          {settled.length > 0 && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowHistory((open) => !open)}
                aria-expanded={showHistory}
                aria-controls={historyId}
                className="rounded-control text-ink-muted hover:text-ink flex min-h-11 w-full items-center justify-between gap-2 text-xs font-medium transition-colors duration-150 md:min-h-9"
              >
                {t("card.tasks.history_title", { count: settled.length })}
                {/* The only motion on the card, and it reports state: which way
                    the disclosure is pointing. Reduced motion is honoured
                    globally in index.css. */}
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    showHistory && "rotate-180",
                  )}
                  aria-hidden="true"
                />
              </button>
              {/* Unmounted while collapsed rather than `hidden`: a closed
                  disclosure on every subject card would otherwise mount the
                  student's whole history N times over for nobody to read. */}
              {showHistory &&
                (historyGroups ? (
                  // One student, one subject, grouped by cut — never a matrix
                  // (PRD-61: the student × material grid lives only in the file).
                  <div id={historyId}>
                    {historyGroups.map((group) => (
                      // More air above a cut than below its heading, and a
                      // hairline between cuts, so the heading groups the rows
                      // under it instead of floating between two lists.
                      <section
                        key={group.key}
                        className="border-hairline mt-4 border-t pt-3 first:mt-1 first:border-t-0 first:pt-0"
                      >
                        <h4 className="text-label mb-0.5">
                          {group.cut
                            ? group.cut.avg_grade === null
                              ? t("card.cuts.heading_empty", { cut: cutFormat.label(group.cut) })
                              : t("card.cuts.heading", {
                                  cut: cutFormat.label(group.cut),
                                  value: formatGrade(group.cut.avg_grade, scale),
                                })
                            : t("card.cuts.outside")}
                        </h4>
                        <ul className="divide-hairline divide-y">
                          {group.rows.map(({ task, state }) => (
                            <TaskRow
                              key={task.task_id}
                              task={task}
                              state={state}
                              scale={scale}
                              classroomId={enrollment.classroom_id}
                              studentId={enrollment.student_id}
                            />
                          ))}
                        </ul>
                      </section>
                    ))}
                  </div>
                ) : (
                  <ul id={historyId} className="divide-hairline divide-y">
                    {settled.map(({ task, state }) => (
                      <TaskRow
                        key={task.task_id}
                        task={task}
                        state={state}
                        scale={scale}
                        classroomId={enrollment.classroom_id}
                        studentId={enrollment.student_id}
                      />
                    ))}
                  </ul>
                ))}
            </div>
          )}
        </>
      )}

      {/* `mt-auto`: the grid stretches both cards to the taller one, so without
          it one card's actions float mid-height while its neighbour's sit at
          the bottom. Now the two rows of buttons always line up. */}
      <div className="mt-auto flex flex-wrap gap-2 pt-4">
        {enrollment.adaptive_level && onReviewAdaptive ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReviewAdaptive(enrollment.student_id, enrollment.classroom_id)}
          >
            {t("adaptive-levels:actions.review_adaptive")}
          </Button>
        ) : null}
        {/* PRD-58 §FR-1.5 — the materials list filtered to this subject
            (`?classroom=` is what useMaterialsUrlState reads). */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/materiales?classroom=${enrollment.classroom_id}`)}
          rightIcon={<ArrowRight className="h-3 w-3" aria-hidden="true" />}
        >
          {t("card.open_materials")}
        </Button>
      </div>
    </article>
  )
})
